/**
 * Renders the login or profile menu item based on authentication status.
 *
 * @param {object} params - The parameters for rendering login or profile.
 * @param {string} status - The authentication status.
 * @param {object} session - The user session object.
 * @param {boolean} isElavon - Flag indicating if the site brand is Elavon.
 * @param {object} styles - The styles object.
 * @param {Function} renderLoginButton - Function to render the login button.
 * @param {Function} renderProfileDropdown - Function to render the profile dropdown.
 * @param {object} profileDropdownProps - Props for the profile dropdown.
 * @returns {JSX.Element | null} The rendered login or profile menu item.
 */
import React from 'react'
import USBButton from '@usb-shield/react-button'
import { LoginModal } from '@/modules/login-modal/LoginModal'
import type { MFAWidgetProps } from '@/components/LoginWidget/loginWidget.model'
import { USBIconProfile, USBIconClose } from '@usb-shield/react-icons'
import { ProfileDropdownMenu } from '@/components/PageHeader/components/ProfileDropdownMenu'
import { hideOverlayAndMenu } from '@/components/PageHeader/components/HideOverlayAndMenu'
import type { RenderProfileDropdownProps } from '@/components/PageHeader/components/ProfileDropdown.model'

export interface RenderLoginOrProfileParams {
  status: string
  session: unknown
  isElavon: boolean
  styles: { [key: string]: string }
  renderLoginButton: () => JSX.Element | null
  renderProfileDropdown: (props: any) => JSX.Element
  profileDropdownProps: object
}

/**
 * Renders either a login button or a profile dropdown based on the user's session status.
 *
 * @param {RenderLoginOrProfileParams} params - The parameters for rendering the utility.
 * @param {string} params.status - The current status of the session, e.g., 'loading'.
 * @param {object | null} params.session - The session object, if the user is logged in; otherwise, null.
 * @param {boolean} params.isElavon - A flag indicating whether the current context is for Elavon.
 * @param {object} params.styles - The styles object containing CSS class names.
 * @param {() => JSX.Element} params.renderLoginButton - A function that renders the login button.
 * @param {(props: any) => JSX.Element} params.renderProfileDropdown - A function that renders the profile dropdown.
 * @param {any} params.profileDropdownProps - The props to be passed to the profile dropdown component.
 * @returns {JSX.Element | null} The rendered login button or profile dropdown, or null if the status is 'loading'.
 */
export function renderLoginOrProfileUtil({
  status,
  session,
  isElavon,
  styles,
  renderLoginButton,
  renderProfileDropdown,
  profileDropdownProps,
}: RenderLoginOrProfileParams): JSX.Element | null {
  if (status === 'loading') return null

  return (
    <>
      {!session && !isElavon && (
        <li key="login">
          <div className={styles.navButton}>{renderLoginButton()}</div>
        </li>
      )}
      {session && renderProfileDropdown(profileDropdownProps)}
    </>
  )
}

/**
 * Renders the login button for the header tier top.
 *
 * @param {object} params - The parameters for rendering the login button.
 * @param {boolean} isLoginSignUpDisabled - Flag to disable login/signup functionality.
 * @param {object} router - The Next.js router object.
 * @param {object} styles - The styles object.
 * @param {React.RefObject<any>} loginButtonRef - Ref for the login button.
 * @param {Function} openLoginModal - Function to open the login modal.
 * @param {boolean} featureMFA - Flag indicating if MFA is enabled.
 * @param {React.RefObject<any>} firstInputRef - Ref for the first input in the modal.
 * @param {object} pageHeaderStyles - Styles for the page header.
 * @param {object} authWidgetConfigData - Auth widget config data.
 * @param {object} mfaWidgetProps - MFA widget props.
 * @returns {JSX.Element | null} The rendered login button and modal.
 */
export interface RenderLoginButtonParams {
  isLoginSignUpDisabled: boolean
  router: { pathname: string }
  styles: { [key: string]: string }
  loginButtonRef: React.RefObject<any>
  openLoginModal: (params: {
    setLoginModalIsOpen: (status: boolean) => void
    setOverlayRendered: (status: boolean) => void
    listMainMenu: React.RefObject<any>
    featureMFA: boolean
  }) => void
  featureMFA: boolean
  firstInputRef: React.RefObject<any>
  pageHeaderStyles: { [key: string]: string }
  authWidgetConfigData: object
  mfaWidgetProps: MFAWidgetProps
  setLoginModalIsOpen: (status: boolean) => void
  setOverlayRendered: (status: boolean) => void
  listMainMenu: React.RefObject<any>
  closeLoginModal: (params: {
    loginButtonRef: React.RefObject<any>
    setOverlayRendered: (status: boolean) => void
  }) => void
}

/**
 * Renders the login button utility and associated modal components.
 *
 * @param {RenderLoginButtonParams} params - The parameters for rendering the login button utility.
 * @param {boolean} params.isLoginSignUpDisabled - Flag indicating if login/signup functionality is disabled.
 * @param {NextRouter} params.router - The Next.js router object for navigation.
 * @param {Record<string, string>} params.styles - Styles object for applying CSS classes.
 * @param {React.RefObject<HTMLButtonElement>} params.loginButtonRef - Ref for the login button element.
 * @param {Function} params.openLoginModal - Function to handle opening the login modal.
 * @param {boolean} params.featureMFA - Flag indicating if multi-factor authentication (MFA) is enabled.
 * @param {React.RefObject<HTMLElement>} params.firstInputRef - Ref for the first input element in the modal.
 * @param {Record<string, string>} params.pageHeaderStyles - Styles object for the page header.
 * @param {unknown} params.authWidgetConfigData - Configuration data for the authentication widget.
 * @param {unknown} params.mfaWidgetProps - Properties for the MFA widget.
 * @param {Function} params.setLoginModalIsOpen - Function to set the state of the login modal's visibility.
 * @param {Function} params.setOverlayRendered - Function to set the state of the overlay rendering.
 * @param {Function} params.listMainMenu - Function to list the main menu items.
 * @param {Function} params.closeLoginModal - Function to handle closing the login modal.
 * @returns {JSX.Element | null} The rendered login button utility or null if conditions are not met.
 */
export function renderLoginButtonUtil({
  isLoginSignUpDisabled,
  router,
  styles,
  loginButtonRef,
  openLoginModal,
  featureMFA,
  firstInputRef,
  pageHeaderStyles,
  authWidgetConfigData,
  mfaWidgetProps,
  setLoginModalIsOpen,
  setOverlayRendered,
  listMainMenu,
  closeLoginModal,
}: RenderLoginButtonParams): JSX.Element | null {
  if (isLoginSignUpDisabled) return null
  if (router.pathname === '/login') return null

  return (
    <>
      <USBButton
        emphasis="subtle"
        ctaStyle="standard"
        size="small"
        id="login-btn"
        addClasses={styles.loginButton}
        forwardedRef={loginButtonRef}
        handleClick={function handleLoginClick() {
          openLoginModal({
            setLoginModalIsOpen,
            setOverlayRendered,
            listMainMenu,
            featureMFA,
          })
        }}
        dataTestId="onLoginClick"
      >
        {' '}
        Log in{' '}
      </USBButton>
      <div
        className={
          featureMFA
            ? styles.loginModalMFA + ' loginModal'
            : styles.loginModal + ' loginModal'
        }
      >
        <USBButton
          type="button"
          id="close-login-modal"
          dataTestId="close-login-modal"
          ariaLabel="Close login modal"
          addClasses={styles.closeLoginModal}
          handleClick={function handleCloseLoginModal() {
            closeLoginModal({
              loginButtonRef,
              setOverlayRendered,
            })
          }}
          forwardedRef={firstInputRef}
        >
          <span aria-hidden="true">×</span>
        </USBButton>
        <LoginModal
          overlayDarkModeClass={pageHeaderStyles.twoTierDarkMode}
          authWidgetConfigData={authWidgetConfigData}
          mfaWidgetProps={mfaWidgetProps}
        />
      </div>
    </>
  )
}

/**
 * Handles toggling of the profile details dropdown and updates the profile arrow class.
 *
 * @param {object} params - The parameters for toggling profile details.
 * @param {React.SyntheticEvent} params.e - The event object.
 * @param {React.RefObject<any>} params.listMainMenu - Ref to the main menu list.
 * @param {(status: boolean) => void} params.setOverlayRendered - Function to set overlay rendered state.
 * @param {boolean} params.profileMenuArrowFlag - Current state of the profile menu arrow flag.
 * @param {(flag: boolean) => void} params.setProfileMenuArrowFlag - Function to set the profile menu arrow flag.
 * @param {boolean} params.overlayRendered - Current overlay rendered state.
 * @param {boolean} params.isProfileMenuOpen - Current open state of the profile menu.
 * @param {(className: string) => void} params.setProfileArrowClass - Function to set the profile arrow class.
 * @param {object} params.styles - Styles object containing arrow class names.
 * @param {Function} params.toggleProfileDetails - Function to toggle profile details.
 */
export function handleToggleProfileDetailsUtil({
  e,
  listMainMenu,
  setOverlayRendered,
  profileMenuArrowFlag,
  setProfileMenuArrowFlag,
  overlayRendered,
  isProfileMenuOpen,
  setProfileArrowClass,
  styles,
  toggleProfileDetails,
}: {
  e: React.SyntheticEvent
  listMainMenu: React.RefObject<any>
  setOverlayRendered: (status: boolean) => void
  profileMenuArrowFlag: boolean
  setProfileMenuArrowFlag: (flag: boolean) => void
  overlayRendered: boolean
  isProfileMenuOpen: boolean
  setProfileArrowClass: (className: string) => void
  styles: { profileNameDownArrow: string; profileNameUpArrow: string }
  toggleProfileDetails: (params: {
    e: React.SyntheticEvent
    listMainMenu: React.RefObject<any>
    setOverlayRendered: (status: boolean) => void
    profileMenuArrowFlag: boolean
    setProfileMenuArrowFlag: (flag: boolean) => void
    overlayRendered: boolean
  }) => void
}): boolean {
  toggleProfileDetails({
    e,
    listMainMenu,
    setOverlayRendered,
    profileMenuArrowFlag,
    setProfileMenuArrowFlag,
    overlayRendered,
  })

  if (isProfileMenuOpen) {
    setProfileArrowClass(styles.profileNameDownArrow)
  } else {
    setProfileArrowClass(styles.profileNameUpArrow)
  }

  return !isProfileMenuOpen
}

/**
 * Handles opening the menu and hiding overlay and menu elements.
 *
 * @param {Object} params - The parameters for opening the menu.
 * @param {Function} setIsMenuOpen - Function to set the menu open state.
 * @param {boolean} profileMenuArrowFlag - The current state of the profile menu arrow flag.
 * @param {Function} setProfileMenuArrowFlag - Function to set the profile menu arrow flag.
 * @param {Function} setOverlayRendered - Function to set the overlay rendered state.
 * @param {React.RefObject<any>} listMainMenu - Reference to the main menu list.
 * @param {React.RefObject<any>} loginButtonRef - Reference to the login button.
 */
export function handleMenuOpenCallbackUtil({
  setIsMenuOpen,
  profileMenuArrowFlag,
  setProfileMenuArrowFlag,
  setOverlayRendered,
  listMainMenu,
  loginButtonRef,
}: {
  setIsMenuOpen: (open: boolean) => void
  profileMenuArrowFlag: boolean
  setProfileMenuArrowFlag: (flag: boolean) => void
  setOverlayRendered: (status: boolean) => void
  listMainMenu: React.RefObject<any>
  loginButtonRef: React.RefObject<any>
}): void {
  setIsMenuOpen(true)
  hideOverlayAndMenu({
    profileMenuArrowFlag,
    setProfileMenuArrowFlag,
    setOverlayRendered,
    listMainMenu,
    loginButtonRef,
  })
}

/**
 * Handles closing the menu by setting the menu open state to false.
 *
 * @param {Function} setIsMenuOpen - Function to set the menu open state.
 */
export function handleMenuCloseCallbackUtil({
  setIsMenuOpen,
}: {
  setIsMenuOpen: (open: boolean) => void
}): void {
  setIsMenuOpen(false)
}

/**
 * Closes the dropdown by calling the menu close callback.
 *
 * @param {Function} handleMenuCloseCallback - Function to handle menu close.
 */
export function closeDropdownUtil({
  handleMenuCloseCallback,
}: {
  handleMenuCloseCallback: () => void
}): void {
  handleMenuCloseCallback()
}

/**
 * Renders the profile dropdown menu.
 *
 * @param {object} props - The properties for rendering the profile dropdown.
 * @returns {JSX.Element} The rendered profile dropdown menu.
 */
export function renderProfileDropdown(
  props: RenderProfileDropdownProps
): JSX.Element {
  const {
    profileDetailsStyled,
    profileArrowClass,
    firstName,
    lastName,
    isElavon,
    handleToggleProfileDetails,
    session,
    styles,
    profileMenuArrowFlag,
    setProfileMenuArrowFlag,
    setOverlayRendered,
    listMainMenu,
    loginButtonRef,
    onLogoutClick,
    signOut,
    router,
    showSuccessToast,
    queryClient,
  } = props

  return (
    <li key="profile">
      <div className={profileDetailsStyled} id="profile-details">
        <a
          className={styles.profileNameTierTop}
          href="#"
          role="button"
          data-testid="toggleProfileDetails"
          onClick={handleToggleProfileDetails}
        >
          {isElavon && (
            <USBIconProfile
              colorVariant="grey80"
              size={24}
              addClasses={styles.profileIcon}
            />
          )}
          {!isElavon && (
            <USBIconProfile
              colorVariant="grey80"
              size={25}
              addClasses={styles.profileIcon}
            />
          )}
          <span className={profileArrowClass}>
            {firstName + ' ' + lastName}
          </span>
        </a>
        <div className={`${styles.profileDropdownTierTop} profileDropdown`}>
          <div className={styles.profileUserSection}>
            <div className={styles.profileUserInfo}>
              <p className={styles.profileUserName}>
                {firstName + ' ' + lastName}
              </p>
              <p className={styles.profileUserEmail}>
                {session?.decodedAccessToken?.email}
              </p>
            </div>
            <USBButton
              type="button"
              id="close-login-modal"
              dataTestId="close-profile-modal"
              addClasses={styles.closeLoginModal}
              handleClick={(e: any) => {
                if (e && typeof e.preventDefault === 'function') {
                  e.preventDefault()
                }
                handleToggleProfileDetails(e)
              }}
            >
              <span aria-hidden="true">
                {!isElavon && 'x'}
                {isElavon && (
                  <USBIconClose
                    colorVariant="grey80"
                    size={24}
                    addClasses={styles.profileIcon}
                  />
                )}
              </span>
            </USBButton>
          </div>
          <div className={styles.profilemenu}>
            <div className={styles.profileDropdownLinks}>
              <ProfileDropdownMenu
                hideOverlayAndMenu={() =>
                  hideOverlayAndMenu({
                    profileMenuArrowFlag,
                    setProfileMenuArrowFlag,
                    setOverlayRendered,
                    listMainMenu,
                    loginButtonRef,
                  })
                }
                onLogoutClick={() =>
                  onLogoutClick({
                    setOverlayRendered,
                    signOut,
                    router,
                    showSuccessToast,
                    queryClient,
                  })
                }
                profileMenuArrowFlag={profileMenuArrowFlag}
                setProfileMenuArrowFlag={setProfileMenuArrowFlag}
                setOverlayRendered={setOverlayRendered}
                listMainMenu={listMainMenu}
                loginButtonRef={loginButtonRef}
              />
            </div>
          </div>
        </div>
      </div>
    </li>
  )
}

/**
 * Renders the authentication section (login button or profile dropdown) for the header.
 *
 * @param {object} params - The parameters for rendering the auth section.
 * @returns {JSX.Element | null} The rendered auth section.
 */
export function renderAuthSection({
  status,
  session,
  isElavon,
  isLoginSignUpDisabled,
  router,
  styles,
  loginButtonRef,
  openLoginModal,
  featureMFA,
  firstInputRef,
  pageHeaderStyles,
  authWidgetConfigData,
  mfaWidgetProps,
  setLoginModalIsOpen,
  setOverlayRendered,
  listMainMenu,
  closeLoginModal,
  profileDetailsStyled,
  profileArrowClass,
  firstName,
  lastName,
  handleToggleProfileDetails,
  profileMenuArrowFlag,
  setProfileMenuArrowFlag,
  onLogoutClick,
  signOut,
  showSuccessToast,
  queryClient,
}: {
  status: string
  session: unknown
  isElavon: boolean
  isLoginSignUpDisabled: boolean
  router: any
  styles: Record<string, string>
  loginButtonRef: React.RefObject<any>
  openLoginModal: (params: {
    setLoginModalIsOpen: (status: boolean) => void
    setOverlayRendered: (status: boolean) => void
    listMainMenu: React.RefObject<any>
    featureMFA: boolean
  }) => void
  featureMFA: boolean
  firstInputRef: React.RefObject<HTMLInputElement>
  pageHeaderStyles: Record<string, string>
  authWidgetConfigData: object
  mfaWidgetProps: MFAWidgetProps
  setLoginModalIsOpen: (open: boolean) => void
  setOverlayRendered: (status: boolean) => void
  listMainMenu: React.RefObject<any>
  closeLoginModal: (params: {
    loginButtonRef: React.RefObject<any>
    setOverlayRendered: (status: boolean) => void
  }) => void
  profileDetailsStyled: string
  profileArrowClass: string
  firstName: string
  lastName: string
  handleToggleProfileDetails: (e: unknown) => void
  profileMenuArrowFlag: boolean
  setProfileMenuArrowFlag: (flag: boolean) => void
  onLogoutClick: (params: {
    setOverlayRendered: (status: boolean) => void
    signOut: <R extends boolean = true>(
      options?: any
    ) => Promise<R extends true ? undefined : any>
    router: any
    showSuccessToast: (message: string) => void
    queryClient: { removeQueries: () => void }
  }) => Promise<void>
  signOut: <R extends boolean = true>(
    options?: any
  ) => Promise<R extends true ? undefined : any>
  showSuccessToast: (message: string) => void
  queryClient: { removeQueries: () => void }
}): JSX.Element | null {
  if (status === 'loading') {
    return null
  }
  return (
    <>
      {!session && !isElavon && (
        <li key="login">
          <div className={styles.navButton}>
            {renderLoginButtonUtil({
              isLoginSignUpDisabled,
              router,
              styles,
              loginButtonRef,
              openLoginModal,
              featureMFA,
              firstInputRef,
              pageHeaderStyles,
              authWidgetConfigData,
              mfaWidgetProps,
              setLoginModalIsOpen,
              setOverlayRendered,
              listMainMenu,
              closeLoginModal,
            })}
          </div>
        </li>
      )}
      {session &&
        renderProfileDropdown({
          profileDetailsStyled,
          profileArrowClass,
          firstName,
          lastName,
          isElavon,
          handleToggleProfileDetails,
          session,
          styles,
          profileMenuArrowFlag,
          setProfileMenuArrowFlag,
          setOverlayRendered,
          listMainMenu,
          loginButtonRef,
          onLogoutClick,
          signOut,
          router,
          showSuccessToast,
          queryClient,
        })}
    </>
  )
}
